import React from 'react';
import { Facebook, Instagram, Linkedin, Twitter, ArrowUp, Mail, MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

const pageLinks = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Projects', to: '/projects' },
  { label: 'Contact', to: '/contact' },
];

const studioLinks = ['Site Strategy', 'Residential', 'Commercial', 'Partnerships'];

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const logoSrc = `${process.env.PUBLIC_URL}/asserts/logo-transparent.png`;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-primary pb-10 pt-24 text-secondary">
      <div className="w-full px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/5 p-8 md:p-10 lg:flex lg:items-center lg:justify-between lg:gap-10">
          <div className="max-w-3xl">
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/85">
              Ready to talk land, timing, and delivery?
            </p>
            <h2 className="mt-4 text-3xl text-secondary md:text-5xl">
              The next conversation is clearer when it starts with the real brief.
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-8 text-secondary/68">
              Share your site, your timeline, or the kind of project you are trying to evaluate. We will come back with a grounded next step.
            </p>
          </div>
          <div className="mt-8 lg:mt-0">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-full bg-accent px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-secondary transition-all duration-300 hover:bg-gold-light hover:text-primary"
            >
              Start a Conversation
            </Link>
          </div>
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-[1.3fr_0.7fr_0.7fr_0.9fr]">
          <div>
            <Link to="/" className="flex items-center justify-center">
              <div className="flex h-28 w-28 items-center justify-center rounded-[2rem] border border-white/10 bg-white/6">
                <img
                  src={logoSrc}
                  alt="Suraksha Builders logo"
                  className="h-20 w-20 object-contain"
                />
              </div>
            </Link>

            <p className="mt-6 max-w-md text-base leading-8 text-secondary/62">
              A Bangalore builder focused on premium residences, commercial anchors, and land-conscious project planning.
            </p>

            <div className="mt-8 flex items-center gap-3">
              {[Facebook, Instagram, Twitter, Linkedin].map((Icon, index) => (
                <a
                  key={index}
                  href="#"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-secondary/65 transition-all duration-300 hover:border-gold-light hover:bg-gold-light hover:text-primary"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/80">Pages</p>
            <ul className="mt-6 space-y-4">
              {pageLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className="text-secondary/65 transition-colors hover:text-gold-light">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/80">Studio focus</p>
            <ul className="mt-6 space-y-4 text-secondary/65">
              {studioLinks.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/80">Reach us</p>
            <div className="mt-6 space-y-5 text-secondary/65">
              <a href="tel:+919535274780" className="flex items-start gap-3 hover:text-gold-light">
                <Phone size={18} className="mt-1 text-gold-light" />
                <span>+91 95352 74780</span>
              </a>
              <a href="mailto:surakshabd2023@gmail.com" className="flex items-start gap-3 hover:text-gold-light">
                <Mail size={18} className="mt-1 text-gold-light" />
                <span>surakshabd2023@gmail.com</span>
              </a>
              <div className="flex items-start gap-3">
                <MapPin size={18} className="mt-1 text-gold-light" />
                <span>Site No. 33, Coconut Garden, Kodigehalli Main Road, Ayyapanagara, K. R. Puram, Bangalore - 560 036</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-6 border-t border-white/10 pt-8 md:flex-row md:items-center md:justify-between">
          <p className="text-[11px] uppercase tracking-[0.24em] text-secondary/35">
            Copyright {currentYear} Suraksha Builders &amp; Developers.
          </p>
          <button
            onClick={scrollToTop}
            className="inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-gold-light/80 transition-colors hover:text-gold-light"
          >
            Back to top
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10">
              <ArrowUp size={16} />
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
}
